import React from "react";
import {
  Moon,
  Gift,
  Calendar,
  Heart,
  Star,
  Users,
  TrendingUp,
} from "lucide-react";
import { firebaseStorage } from "../utils/firebase";

const LandingPage = ({ onStart, onTrack, onEdit, successCode }) => {
  const [mounted, setMounted] = React.useState(false);
  const [stats, setStats] = React.useState({
    totalGifts: 0,
    totalUsers: 0,
    activeToday: 0,
  });
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setMounted(true);
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const gifts = await firebaseStorage.getAllGifts();
      const realGifts = gifts.filter(g => !g.isDemo);
      const approvedGifts = realGifts.filter(
        (g) => g.paymentStatus === "approved" || g.planType === "free",
      );

      // حساب عدد المستخدمين الفريدين (بناءً على أرقام التليفون)
      const uniquePhones = new Set();
      realGifts.forEach((g) => {
        const phone = g.paymentProof?.senderNumber || g.phoneNumber;
        if (phone) uniquePhones.add(phone);
      });

      // حساب الهدايا النشطة اليوم (تم إنشاؤها خلال 24 ساعة)
      const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
      const activeToday = realGifts.filter((g) => {
        const createdAt = g.createdAt?.toDate
          ? g.createdAt.toDate().getTime()
          : g.createdAt;
        return createdAt >= oneDayAgo;
      }).length;

      setStats({
        totalGifts: approvedGifts.length,
        totalUsers: uniquePhones.size || approvedGifts.length,
        activeToday: activeToday,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error loading stats:", error);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-950 via-purple-950 to-indigo-900 text-white relative overflow-hidden">
      {/* Stars Background */}
      <div className="absolute inset-0 overflow-hidden">
        {mounted &&
          [...Array(50)].map((_, i) => (
            <Star
              key={i}
              className="absolute text-yellow-200 opacity-60 animate-pulse"
              size={Math.random() * 8 + 4}
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${Math.random() * 2 + 2}s`,
              }}
            />
          ))}
      </div>

      <div className="relative z-10 container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <Moon className="w-16 h-16 text-yellow-300 animate-pulse" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-yellow-400 mb-2 py-4 leading-loose tracking-wide">
            Two Souls
          </h1>
          <p className="text-xl md:text-2xl text-yellow-100 max-w-2xl mx-auto">
            رمضان أجمل مع من نحب
          </p>
          <p className="text-lg md:text-xl text-purple-200 max-w-2xl mx-auto mt-2">
            Ramadan is better with those we love
          </p>
        </div>

        {successCode && (
          <div className="bg-white/10 p-6 rounded-2xl mb-8 text-center max-w-md mx-auto backdrop-blur-md">
            <h3 className="text-xl font-bold text-yellow-400 mb-2">
              تم استلام طلبك بنجاح! 🎉
            </h3>
            <p className="text-white/80 text-sm mb-4">
              احتفظ بكود التتبع لمتابعة حالة الهدية:
            </p>
            <div className="flex items-center justify-center gap-2 bg-black/20 p-4 rounded-xl">
              <span className="text-2xl font-mono font-bold text-white tracking-widest">
                {successCode}
              </span>
              <button
                onClick={(e) => {
                  navigator.clipboard.writeText(successCode);
                  const btn = e.currentTarget;
                  const oldHtml = btn.innerHTML;
                  btn.innerHTML = "✅";
                  setTimeout(() => {
                    btn.innerHTML = oldHtml;
                  }, 2000);
                }}
                className="w-10 h-10 flex items-center justify-center bg-yellow-400 rounded-lg hover:bg-yellow-500 transition-all active:scale-95 flex-shrink-0"
                title="نسخ الكود"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1e1b4b"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                </svg>
              </button>
            </div>
            <p className="text-[10px] text-purple-300 mt-4 italic">
              * يمكنك استخدام هذا الكود في زر "تتبع طلبك" بالأسفل
            </p>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-16">
          <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 text-center hover:bg-white/20 transition-all">
            <Gift className="w-12 h-12 mx-auto mb-4 text-yellow-300" />
            <h3 className="text-xl font-semibold mb-2">30 يوماً من الحب</h3>
            <p className="text-purple-200">30 Days of Love</p>
          </div>
          <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 text-center hover:bg-white/20 transition-all">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-yellow-300" />
            <h3 className="text-xl font-semibold mb-2">كل يوم مفاجأة</h3>
            <p className="text-purple-200">Daily Surprises</p>
          </div>
          <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 text-center hover:bg-white/20 transition-all">
            <Heart className="w-12 h-12 mx-auto mb-4 text-yellow-300" />
            <h3 className="text-xl font-semibold mb-2">هدية شخصية</h3>
            <p className="text-purple-200">Personal Gift</p>
          </div>
        </div>

        {/* Live Stats Section */}
        {!loading && (
          <div className="max-w-4xl mx-auto mb-16">
            <div className="bg-gradient-to-br from-purple-900/50 to-indigo-900/50 backdrop-blur-lg rounded-2xl p-8 border border-white/10 shadow-2xl">
              <h3 className="text-2xl font-bold text-center mb-8 text-yellow-300">
                📊 إحصائيات مباشرة
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500/20 rounded-full mb-4">
                    <Gift className="w-8 h-8 text-yellow-300" />
                  </div>
                  <p className="text-4xl font-bold text-white mb-2 animate-pulse">
                    {stats.totalGifts.toLocaleString()}
                  </p>
                  <p className="text-purple-200 text-sm">هدية تم إنشاؤها</p>
                  <p className="text-purple-300 text-xs mt-1">Gifts Created</p>
                </div>

                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/20 rounded-full mb-4">
                    <Users className="w-8 h-8 text-blue-300" />
                  </div>
                  <p className="text-4xl font-bold text-white mb-2 animate-pulse">
                    {stats.totalUsers.toLocaleString()}+
                  </p>
                  <p className="text-purple-200 text-sm">مستخدم سعيد</p>
                  <p className="text-purple-300 text-xs mt-1">Happy Users</p>
                </div>

                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-4">
                    <TrendingUp className="w-8 h-8 text-green-300" />
                  </div>
                  <p className="text-4xl font-bold text-white mb-2 animate-pulse">
                    {stats.activeToday.toLocaleString()}
                  </p>
                  <p className="text-purple-200 text-sm">هدية اليوم</p>
                  <p className="text-purple-300 text-xs mt-1">Today's Gifts</p>
                </div>
              </div>

              <div className="mt-6 text-center">
                <p className="text-yellow-200 text-sm font-medium animate-pulse">
                  🎉 انضم لآلاف المستخدمين السعداء!
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto items-center justify-center">
          <button
            onClick={onStart}
            className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-indigo-950 font-bold text-xl px-4 py-4 rounded-full shadow-2xl hover:shadow-yellow-500/50 transition-all transform hover:scale-105 flex items-center justify-center gap-3"
          >
            <span>ابدأ الآن</span>
          </button>
          <button
            onClick={onTrack}
            className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-indigo-950 font-bold text-xl px-4 py-4 rounded-full shadow-2xl hover:shadow-yellow-500/50 transition-all transform hover:scale-105 flex items-center justify-center gap-3"
          >
            <span>تتبع طلبك</span>
          </button>
          <button
            onClick={onEdit}
            className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-bold text-xl px-4 py-4 rounded-full shadow-2xl hover:shadow-purple-500/50 transition-all transform hover:scale-105 flex items-center justify-center gap-3"
          >
            <span>عدّل هديتك</span>
          </button>
        </div>

        {/* How to use section - UPDATED */}
        <div className="max-w-6xl mx-auto mt-20 px-4">
          <h3 className="text-3xl font-bold text-center mb-12 text-yellow-300">
            كيف تستخدم التطبيق؟
          </h3>

          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-right"
            dir="rtl"
          >
            {/* خطوة 1 */}
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-all flex flex-col items-start">
              <div className="w-14 h-14 bg-yellow-400/20 rounded-full flex items-center justify-center text-3xl font-bold text-yellow-300 mb-6">
                1
              </div>
              <h4 className="text-2xl font-bold mb-4 text-white border-b border-white/10 pb-2 w-full text-right">
                اختر الخطة المناسبة
              </h4>
              <div className="space-y-2 text-purple-100 text-base font-bold text-right w-full">
                <p>• مجاني: 5 هدايا (نصوص فقط)</p>
                <p>• بيزك (50 جنيه): 15 هدية + صور</p>
                <p>• بلس (100 جنيه): 30 هدية + صور + أدعية</p>
              </div>
            </div>

            {/* خطوة 2 */}
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-all flex flex-col items-start">
              <div className="w-14 h-14 bg-yellow-400/20 rounded-full flex items-center justify-center text-3xl font-bold text-yellow-300 mb-6">
                2
              </div>
              <h4 className="text-2xl font-bold mb-4 text-white border-b border-white/10 pb-2 w-full text-right">
                ادفع (للخطط المدفوعة)
              </h4>
              <div className="space-y-2 text-purple-100 text-base font-bold text-right w-full">
                <p>• حول على فودافون كاش للرقم المعروض</p>
                <p>• احتفظ برقم العملية ورقمك</p>
                <p>• سيتم مراجعة الدفع خلال 24 ساعة</p>
              </div>
            </div>

            {/* خطوة 3 */}
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-all flex flex-col items-start">
              <div className="w-14 h-14 bg-yellow-400/20 rounded-full flex items-center justify-center text-3xl font-bold text-yellow-300 mb-6">
                3
              </div>
              <h4 className="text-2xl font-bold mb-4 text-white border-b border-white/10 pb-2 w-full text-right">
                املأ معلومات الهدية
              </h4>
              <div className="space-y-2 text-purple-100 text-base font-bold text-right w-full">
                <p>• اسمك واسم من تهديه</p>
                <p>• اختر نوع العلاقة (أم، أب، صديق...)</p>
                <p>• اكتب رسالة الافتتاح</p>
                <p>• (اختياري) أضف كلمة سر لحماية الهدية 🔒</p>
              </div>
            </div>

            {/* خطوة 4 */}
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-all flex flex-col items-start">
              <div className="w-14 h-14 bg-yellow-400/20 rounded-full flex items-center justify-center text-3xl font-bold text-yellow-300 mb-6">
                4
              </div>
              <h4 className="text-2xl font-bold mb-4 text-white border-b border-white/10 pb-2 w-full text-right">
                املأ الهدايا اليومية
              </h4>
              <div className="space-y-2 text-purple-100 text-base font-bold text-right w-full">
                <p>• استخدم القوالب الجاهزة لتوفير الوقت! 🚀</p>
                <p>• أو اكتب رسائلك الخاصة</p>
                <p>• أضف صور (للخطط المدفوعة)</p>
                <p>• أضف أدعية (خطة بلس فقط)</p>
              </div>
            </div>

            {/* خطوة 5 */}
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-all flex flex-col items-start">
              <div className="w-14 h-14 bg-yellow-400/20 rounded-full flex items-center justify-center text-3xl font-bold text-yellow-300 mb-6">
                5
              </div>
              <h4 className="text-2xl font-bold mb-4 text-white border-b border-white/10 pb-2 w-full text-right">
                احصل على كود التتبع
              </h4>
              <div className="space-y-2 text-purple-100 text-base font-bold text-right w-full">
                <p>• سيظهر لك كود تتبع (مثال: TS-1234)</p>
                <p>• احتفظ به لمتابعة حالة الطلب</p>
                <p>• استخدمه في زر "تتبع طلبك"</p>
              </div>
            </div>

            {/* خطوة 6 */}
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-all flex flex-col items-start md:col-span-2 lg:col-span-1">
              <div className="w-14 h-14 bg-yellow-400/20 rounded-full flex items-center justify-center text-3xl font-bold text-yellow-300 mb-6">
                6
              </div>
              <h4 className="text-2xl font-bold mb-4 text-white border-b border-white/10 pb-2 w-full text-right">
                شارك الرابط
              </h4>
              <div className="space-y-2 text-purple-100 text-base font-bold text-right w-full">
                <p>• بعد الموافقة، احصل على رابط الهدية</p>
                <p>• شارك الرابط العادي أو Magic Link</p>
                <p>• Magic Link يفتح الهدية مباشرة بدون كلمة سر! 🔗</p>
              </div>
            </div>
          </div>

          {/* ملاحظة إضافية */}
          <div
            className="mt-8 bg-yellow-400/10 border border-yellow-400/30 rounded-2xl p-6 text-right"
            dir="rtl"
          >
            <h4 className="text-xl font-bold text-yellow-300 mb-3">
              💡 نصيحة مهمة
            </h4>
            <p className="text-purple-100 leading-relaxed">
              الخطط المدفوعة تحتاج موافقة الإدارة خلال 24 ساعة. استخدم كود
              التتبع لمعرفة حالة طلبك. إذا تمت الموافقة، ستحصل على رابط الهدية
              فوراً!
            </p>
          </div>
        </div>

        {/* Developer Credit */}
        <div className="mt-8 group">
          <a
            href="https://www.tiktok.com/@two_2_souls"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-1 opacity-30 hover:opacity-100 transition-all"
          >
            <span className="text-[10px] tracking-[0.4em] text-gray-400 uppercase font-light">
              Developed by
            </span>
            <span className="text-white text-xs tracking-[0.3em] font-light group-hover:text-[#d4af37]">
              TWO <span className="font-bold">SOULS</span>
            </span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
